import React, { useEffect, useState } from "react";
import { Col, Form, Row, Spinner, Badge } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { authHeader } from "../../../services/auth-header";
import { BASE_URL } from "../../../services/Api";
import CommonSalesQuotationInternationalFields from "./CommonSalesQuotationInternationalFields";

const UpdatePumpInternationalQuotation = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        customer: null,
        customerDetails: {
            contactperson: '',
            contactno: '',
            email: '',
            freight: '',
            packingandforwarding: '',
            paymentterm: '',
            incoterms: ''
        },
        customerMultipleAddress: null,
        rows: [],
        exchangeRate: 1,
        exportchargescurrency: 0,
        exportpreparationchargesinr: '',
        selectedCurrency: " "

    });
    const [quotationno, setQuotationno] = useState('');
    const [quotationdate, setQuotationdate] = useState('');
    const [quotevalidity, setQuotevalidity] = useState('');
    const [status, setStatus] = useState('Open');

    // Fetch existing quotation data
    const fetchQuotationData = async () => {
        try {
            const response = await axios.get(`${BASE_URL}quotation/findById/${id}`, {
                headers: authHeader()
            });

            const data = response.data;

            // Set form data from API response
            setQuotationno(data.quotationno);
            setQuotationdate(data.quotationdate?.split('T')[0] || '');
            setQuotevalidity(data.quotevalidity || '');
            setStatus(data.status || 'Open');

            setFormData({
                customer: data.customer ? {
                    value: data.customer.id,
                    label: data.customer.companyname
                } : null,
                customerDetails: {
                    contactperson: data.contactperson || '',
                    contactno: data.contactno || '',
                    email: data.email || '',
                    freight: data.freight || '',
                    packingandforwarding: data.packingandforwarding || '',
                    paymentterm: data.paymentterm || '',
                    incoterms: data.incoterms || ''
                },
                customerMultipleAddress: data.customeraddress ? {
                    value: data.customeraddress.id,
                    label: data.customeraddress.customeraddress
                } : null,
                rows: data.quotationItems?.map(item => ({
                    value: item.product?.id,
                    label: item.product?.description,
                    description: item.product?.description || '',
                    productremarks: item.productremarks || '',
                    quantity: item.quantity || 0,
                    unitratecurrency: item.unitratecurrency || 0,
                    totalcurrency: item.totalcurrency || 0,
                    exchangerate: item.exchangerate || data.quotationexchangerate || 1,
                    unitRateInr: item.unitrateinr || 0,
                    totalInr: item.totalinr || 0,
                    hsnsacno: item.hsnsacno || '',
                    partno: item.partno || '',
                    serialnumber: item.serialnumber || '',
                    modelnumber: item.modelnumber || '',
                    tagnumber: item.tagnumber || '',
                    moc: item.moc || '',
                    itemcode: item.itemcode || '',
                    uom: item.uom || '',
                    currency: item.currency || data.quotationcurrency || "$ (USD)"
                })) || [],
                exchangeRate: data.quotationexchangerate || 1,
                exportchargescurrency: data.exportpreparationchargescurrency || 0,
                exportpreparationchargesinr: data.exportpreparationchargesinr || '',
                selectedCurrency: data.quotationcurrency || "$ (USD)"
            });

        } catch (error) {
            console.error("Error fetching quotation data", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to fetch quotation data. Please try again.",
            });
            navigate("/quotationIndexTable/1");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchQuotationData();
    }, [id]);

    // Handle data changes from child component
    const handleDataChange = (newData) => {
        setFormData(prev => ({
            ...prev,
            ...newData,
            rows: newData.rows || prev.rows
        }));
    };

    // Submit updated quotation
    const handleSubmit = async () => {
        setIsSubmitting(true);

        const {
            customer,
            customerDetails,
            customerMultipleAddress,
            rows,
            exchangeRate,
            exportchargescurrency,
            exportpreparationchargesinr,
            selectedCurrency
        } = formData;

        // Validation
        if (!customer) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Please select a customer',
            });
            setIsSubmitting(false);
            return;
        }

        if (rows.length === 0) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Please add at least one product',
            });
            setIsSubmitting(false);
            return;
        }

        // Calculate totals
        const misccharges = exportchargescurrency * exchangeRate;
        const netTotalInr = rows.reduce((total, row) => total + (row.totalInr || 0), 0);
        const netTotalCurrency = rows.reduce((total, row) => total + (row.totalcurrency || 0), 0);
        const grandTotalInr = netTotalInr + misccharges;
        const grandTotalCurrency = netTotalCurrency + exportchargescurrency;

        // Prepare quotation items
        const quotationItems = rows.map(row => ({
            product: { id: row.value },
            productremarks: row.productremarks || '',
            quantity: row.quantity || 0,
            unitrateinr: row.unitRateInr || 0,
            totalinr: row.totalInr || 0,
            unitratecurrency: row.unitratecurrency || 0,
            totalcurrency: row.totalcurrency || 0,
            exchangerate: row.exchangerate || exchangeRate,
            itemcode: row.itemcode || '',
            hsnsacno: row.hsnsacno || '',
            serialnumber: row.serialnumber || '',
            modelnumber: row.modelnumber || '',
            tagnumber: row.tagnumber || '',
            moc: row.moc || '',
            partno: row.partno || '',
            uom: row.uom || '',
            currency: selectedCurrency,
        }));

        // Prepare main quotation data
        const quotationData = {
            id: id,
            quotationtype: 'Pump Spare',
            quotationcategory: 'International',
            quotationno,
            quotationdate,
            quotevalidity,
            status,
            quotationItems,
            customer: { id: customer.value },
            customeraddress: customerMultipleAddress ? { id: customerMultipleAddress.value } : null,
            contactperson: customerDetails.contactperson,
            contactno: customerDetails.contactno,
            email: customerDetails.email,
            freight: customerDetails.freight,
            packingandforwarding: customerDetails.packingandforwarding,
            paymentterm: customerDetails.paymentterm,
            incoterms: customerDetails.incoterms,
            quotationcurrency: selectedCurrency,
            quotationexchangerate: exchangeRate,
            grandtotalinr: grandTotalInr,
            grandtotalcurrency: grandTotalCurrency,
            exportpreparationchargescurrency: exportchargescurrency,
            exportpreparationchargesinr,
            nettotalinr: netTotalInr,
            nettotalcurrency: netTotalCurrency,
        };

        try {
            await axios.put(`${BASE_URL}quotation/update/${id}`, quotationData, {
                headers: authHeader()
            });

            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Quotation updated successfully!',
            });
            navigate("/quotationIndexTable/1");
        } catch (error) {
            console.error("Error updating quotation:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to update quotation. Please try again.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="container text-center py-5">
                <Spinner animation="border" />
                <p>Loading quotation details...</p>
            </div>
        );
    }

    return (
        <div className="container">
            <Form>
                <Row>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Quotation No.</Form.Label>
                            <Form.Control
                                type="text"
                                value={quotationno}
                                onChange={e => setQuotationno(e.target.value)}
                                readOnly
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Quotation Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={quotationdate}
                                onChange={(e) => setQuotationdate(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Quote validity</Form.Label>
                            <Form.Control
                                type="text"
                                value={quotevalidity}
                                onChange={(e) => setQuotevalidity(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                            >
                                <option value="Open">Open</option>
                                <option value="Submitted">Submitted</option>
                                <option value="Approved">Approved</option>
                                <option value="Rejected">Rejected</option>
                                <option value="Completed">Completed</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>

                <CommonSalesQuotationInternationalFields
                    onDataChange={handleDataChange}
                    initialData={formData}
                    isUpdateMode={true}
                />

                <br />
                <div className="d-flex justify-content-end">
                    <button
                        className="btn btn-primary me-2"
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" />
                                Updating...
                            </>
                        ) : 'UPDATE QUOTATION'}
                    </button>

                    <button
                        className="btn btn-secondary"
                        type="button"
                        onClick={() => navigate("/quotationIndexTable/1")}
                        disabled={isSubmitting}
                    >
                        CANCEL
                    </button>
                </div>
            </Form>
        </div>
    );
};

export default UpdatePumpInternationalQuotation;
